import debug from 'debug';
import Awesomplete from 'awesomplete';

import BaseComponent from 'Global/Shared/Components/BaseComponent/BaseComponent';
import { Debounce } from 'Global/Shared/Components/Debounce/Debounce';
import { queryString } from 'Global/Shared/Components/QueryString/QueryString';
import sitecore from 'Global/Shared/Components/Sitecore/Sitecore';

import soegSvg from 'Minetilbud/Shared/Icons/soeg_outline.svg';

import AutoSuggest from './data/auto-suggest';
import Settings from './data/settings';
import Data from './utils/data';
import AwesompleteEvents from './events/awesomplete';
import SearchUrl from './utils/search-url';

const log = debug('SearchBox');
const autoSuggest = new AutoSuggest();
const debounce = new Debounce().debounce;

class SearchBox extends BaseComponent {
	constructor(element) {
		super(element)

		this.autoCompleteEvent = this.autoCompleteHandler.bind(this);
		this.submitOnButtonClickEvent = this.submitOnButtonClickHandler.bind(this);
		this.submitOnEnterEvent = this.submitOnEnterHandler.bind(this);

		this.autoCompleteHandler = debounce(this.autoCompleteHandler, 250)

		this.initialize();
	}

	/**
	 * @param {Event} event
	 */
	async autoCompleteHandler(event) {
		// check for arrow keys (37-40)
		if (event.keyCode > 36 && event.keyCode < 41) return;
		if (this.dom.field.value.length < Settings.autosuggestCharacters) return;

		try {
			autoSuggest.abort();
			const data = await autoSuggest.get(this.dom.field.value);
			log('autoCompleteHandler()', data)
			if (data === undefined) return;

			const searchOptions = Data.parse(data);

			this.awesomplete.list = searchOptions;
			this.awesomplete.evaluate();
		} catch (error) {
			log('autoCompleteHandler() - ERROR', error);
		}
	}

	eventListeners() {
		this.dom.button.addEventListener('click', this.submitOnButtonClickEvent, false);
		this.dom.field.addEventListener('keydown', this.submitOnEnterEvent, false);
		this.dom.field.addEventListener('keyup', this.autoCompleteEvent, false);

		const awesompleteEvents = new AwesompleteEvents(this.dom.field);
	}

	/**
	 * @returns {Object} settings [autosuggestCharacters, searchQueryParam, searchUrl]
	 */
	getSettings() {
		let settings = {};

		try {
			settings = JSON.parse(this.element.getAttribute('data-settings'));
			log('getSettings()', settings);
			this.settings = settings;
		} catch (error) {
			log('getSettings() - error', error);
			settings = null;
		}

		return settings;
	}

	initialize() {
		if (this.getSettings() === null) return;

		Settings.autosuggestCharacters = this.settings.autosuggestCharacters;
		Settings.searchQueryParam = this.settings.searchQueryParam;
		Settings.searchUrl = this.settings.searchUrl;

		this.initDomCache();
		this.setup();
		this.eventListeners();
	}

	setup() {
		this.dom.button.insertAdjacentHTML('afterBegin', soegSvg);

		const Sitecore = new sitecore();
		if (Sitecore.IsEdit() || Sitecore.IsPreview()) return;

		if (queryString.Get('qw')) {
			this.dom.field.value = queryString.Get('qw');
		}

		this.awesomplete = new Awesomplete(this.dom.field, {
			maxItems: 20,
			sort: false,
			item: (text, input) => {
				let element = document.createElement('li');
				element.setAttribute('aria-selected', false)
				
				// Advert
				if (!text.value.imageUrl) {
					element.className = 'o-search-box__list-item';
					element.innerHTML = `<span class="o-search-box__option-text">${text.label}</span>`;
				} 
				// Only customers have images
				else {
					element.className = 'o-search-box__list-item-customer';
					element.innerHTML = `<img class="o-search-box__option-image" src="${text.value.imageUrl}" /><span class="o-search-box__option-image-text">${text.label}</span>`;
				}
					
				return element;
			}
		});
	}

	/**
	 * @param {Event} event
	 */
	submitOnButtonClickHandler(event) {
		event.preventDefault();
		if ( this.dom.field.value.length > 0 ) {
			window.location.href = SearchUrl.get(this.dom.field.value);
		}
	}

	/**
	 * @param {Event} event
	 */
	submitOnEnterHandler(event) {
		// Enter not pressed
		if (event.keyCode !== 13) return;

		//and we have a value in the search field. This check is to support the case where the user writes text in the input, but does not select
		// any items in the awesomplete list - in that case, we want to just search for the given input
		if (this.dom.field.value.length > 0 ) {
			// Nothing is highlighted in Awesomplete, so we just seach for whatever value is in the input
			if (Settings.isHighlighted === false) {
				window.location.href = SearchUrl.get(this.dom.field.value);
			}
		}

		event.preventDefault();
		return false;
	}

}

export default SearchBox;
